import { useNavigate } from "react-router-dom";
import { User } from "@supabase/supabase-js";
import { useRole } from "@/hooks/useRole";
import { useNotifications } from "@/hooks/useNotifications";
import { usePoints } from "@/hooks/usePoints";
import { usePendingRewards } from "@/hooks/usePendingRewards";
import { AdminBadge } from "@/components/auth/AdminBadge";
import { UserAvatar } from "./UserAvatar";
import { 
  Bell, 
  Users, 
  User as UserIcon, 
  LogOut,
  Trash2,
  BriefcaseIcon,
  ChevronRight,
  Trophy,
  Star,
  // Flame removed — streak feature deprecated
  Sparkles,
  FileText,
  Scale,
  Sun,
  Moon
} from "lucide-react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Separator } from "@/components/ui/separator";
import { NotificationCenter } from "@/components/notifications/NotificationCenter";
import { PlanPill } from "@/components/subscription/PlanPill";
import { CreatorCta } from "@/components/creators/CreatorCta";

import { useState } from "react";
import { useTheme } from "next-themes";

interface MobileUserHubProps {
  user: User;
  onSignOut: () => Promise<void>;
  useCompactLabels?: boolean;
}

interface MenuItemProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  badge?: number;
  variant?: 'default' | 'destructive';
}

function MenuItem({ icon, label, onClick, badge, variant = 'default' }: MenuItemProps) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center justify-between w-full px-4 py-3 rounded-lg transition-colors ${
        variant === 'destructive' 
          ? 'text-destructive hover:bg-destructive/10' 
          : 'hover:bg-muted'
      }`}
    >
      <div className="flex items-center gap-3">
        {icon}
        <span className="font-medium">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        {badge !== undefined && badge > 0 && (
          <span className="bg-primary text-primary-foreground text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </div>
    </button>
  );
}

export function MobileUserHub({ user, onSignOut, useCompactLabels = false }: MobileUserHubProps) {
  const navigate = useNavigate();
  const { role } = useRole();
  const { unreadCount } = useNotifications();
  const { points, level, loading: pointsLoading } = usePoints();
  const { count: claimCount } = usePendingRewards();
  const { resolvedTheme, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsOpen(false);
    setShowNotifications(false);
  };

  const handleSignOut = async () => {
    setIsOpen(false);
    await onSignOut();
  };

  const handleNotificationsClick = () => {
    setIsOpen(false);
    setShowNotifications(true);
  };

  // Get display name from user email
  const displayName = user.email?.split('@')[0] || 'User';

  return (
    <>
      <Drawer open={isOpen} onOpenChange={setIsOpen}>
      <DrawerTrigger asChild>
        <button
          className="flex flex-col items-center gap-1 h-auto py-2 px-2 relative bg-transparent border-none"
          aria-label="Open user menu"
        >
          <div className="relative">
            <UserAvatar size="sm" />
            {(unreadCount > 0 || claimCount > 0) && (
              <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                {(() => {
                  const total = unreadCount + claimCount;
                  return total > 9 ? "9+" : total;
                })()}
              </span>
            )}
          </div>
          <span className="text-[10px] flex items-center gap-1">
            You
            {role === 'admin' && <AdminBadge />}
          </span>
        </button>
      </DrawerTrigger>
      
      <DrawerContent className="max-h-[85vh]">
        <div className="flex flex-col pb-safe">
            <DrawerHeader className="pb-2">
              <div className="flex items-center gap-3">
                <UserAvatar size="lg" />
                <div className="flex flex-col items-start gap-1">
                  <DrawerTitle className="text-lg">Hi, {displayName}!</DrawerTitle>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">{user.email}</span>
                    <PlanPill compact />
                  </div>
                </div>
              </div>
              
              {/* Points/Level Stats Bar */}
              {!pointsLoading && (
                <button
                  onClick={() => handleNavigation('/my-progress')}
                  className="mt-3 flex items-center justify-between w-full px-3 py-2 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                >
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1.5">
                      <Trophy className="h-4 w-4 text-amber-500" />
                      <span className="font-medium">Lvl {level}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Star className="h-4 w-4 text-yellow-500" />
                      <span className="font-medium">{points} pts</span>
                    </div>
                  </div>

                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
              )}
            </DrawerHeader>
            
            <div className="px-4 py-2 space-y-1">
              {/* Primary Actions */}
              <MenuItem
                icon={<Trophy className="h-5 w-5 text-amber-500" />}
                label="My Journey"
                onClick={() => handleNavigation('/my-progress')}
                badge={claimCount}
              />

              <MenuItem
                icon={<Sparkles className="h-5 w-5" />}
                label="Rewards Hub"
                onClick={() => handleNavigation('/rewards')}
              />

              <MenuItem
                icon={<Bell className="h-5 w-5" />}
                label="Notifications"
                onClick={handleNotificationsClick}
                badge={unreadCount}
              />

              <MenuItem
                icon={<Users className="h-5 w-5" />}
                label="My Circle · Assign tasks"
                onClick={() => handleNavigation('/assign')}
              />

              <Separator className="my-2" />
              
              {/* Profile & Settings */}
              <MenuItem
                icon={<UserIcon className="h-5 w-5" />}
                label="My Profile"
                onClick={() => handleNavigation('/profile')}
              />

              <CreatorCta variant="menu" onNavigate={() => setIsOpen(false)} />

              
              
              
              {/* Role-specific items */}
              {role === 'advertiser' && (
                <MenuItem
                  icon={<BriefcaseIcon className="h-5 w-5" />}
                  label="Advertiser Dashboard"
                  onClick={() => handleNavigation('/advertiser-dashboard')}
                />
              )}
              
              {role === 'admin' && (
                <MenuItem
                  icon={<BriefcaseIcon className="h-5 w-5" />}
                  label="Admin Panel"
                  onClick={() => handleNavigation('/admin/partner-analytics')}
                />
              )}
              
              <Separator className="my-2" />

              {/* Theme Toggle */}
              <MenuItem
                icon={resolvedTheme === "dark" ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
                label={resolvedTheme === "dark" ? "Dark Mode" : "Light Mode"}
                onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
              />

              {/* Legal */}
              <MenuItem
                icon={<Scale className="h-5 w-5" />}
                label="Terms of Service"
                onClick={() => handleNavigation('/terms-of-service')}
              />
              <MenuItem
                icon={<FileText className="h-5 w-5" />}
                label="Privacy Policy"
                onClick={() => handleNavigation('/privacy-policy')}
              />
              
              {/* Delete Account */}
              <MenuItem
                icon={<Trash2 className="h-5 w-5" />}
                label="Delete Account"
                onClick={() => handleNavigation('/profile?tab=account')}
                variant="destructive"
              />
              
              {/* Sign Out */}
              <MenuItem
                icon={<LogOut className="h-5 w-5" />}
                label="Sign Out"
                onClick={handleSignOut}
                variant="destructive"
              />
            </div>
            
            {/* Bottom spacing for safe area */}
            <div className="h-4" />
        </div>
      </DrawerContent>
      </Drawer>
      {showNotifications && (
        <NotificationCenter
          autoOpen
          hideTrigger
          onRequestClose={() => setShowNotifications(false)}
        />
      )}
    </>
  );
}
